import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { withCsrfHeader } from "@/lib/queryClient";

export default function SignupSuccess() {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [workspace, setWorkspace] = useState<{ slug: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const verifySession = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get("session_id");

      if (!sessionId) {
        setStatus("error");
        setErrorMessage("No session ID found. Please try again.");
        return;
      }

      try {
        const res = await fetch("/api/signup/verify-session", {
          method: "POST",
          headers: await withCsrfHeader({ "Content-Type": "application/json" }),
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Verification failed");
        }

        setWorkspace(data.workspace);
        setStatus("success");
      } catch (error: any) {
        setStatus("error");
        setErrorMessage(error.message || "Failed to verify payment. Please contact support.");
      }
    };

    verifySession();
  }, []);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-blue-500 mb-4" />
            <p className="text-lg font-medium">Setting up your workspace...</p>
            <p className="text-muted-foreground mt-2">This will only take a moment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl">Something Went Wrong</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">{errorMessage}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => window.location.href = "/signup"} data-testid="button-try-again">
                Try Again
              </Button>
              <Button onClick={() => window.location.href = "mailto:admin@entertaincrm.com"} data-testid="button-contact-support">
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Welcome to Clarik!</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            Your workspace <strong>{workspace?.name}</strong> has been created successfully.
          </p>
          <p className="text-sm text-muted-foreground">
            You can now log in and start managing your entertainment business.
          </p>
          <Button 
            className="w-full" 
            onClick={() => window.location.href = `/w/${workspace?.slug}/login`}
            data-testid="button-login-workspace"
          >
            Log In to Your Workspace
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
